import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, vShow as _vShow, withDirectives as _withDirectives, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "estimate-calculator" }
const _hoisted_2 = { class: "description" }
const _hoisted_3 = { class: "estimate-calculator" }
const _hoisted_4 = { class: "description" }
const _hoisted_5 = {
  key: 0,
  class: "hint"
}
const _hoisted_6 = {
  key: 1,
  class: "estimated-disbursements"
}
const _hoisted_7 = { class: "disbursements" }
const _hoisted_8 = { class: "amount" }
const _hoisted_9 = { class: "term" }
const _hoisted_10 = { class: "info-tile" }
const _hoisted_11 = { key: 0 }

import { ref, reactive, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import useFormatter from "@/use/formatter";
import useValidation from "@/use/validation";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import NextButton from "@/components/shared/NextButton.vue";
import Step from "@/components/shared/Step.vue";
import EstimateCalculator from "@/components/loan-configuration/EstimateCalculator.vue";
import { FieldSchema } from "@/models/form";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import { ProductIds } from "@/config/constants";
import Icon from "@/components/shared/Icon.vue";
import Tile from "@/components/shared/Tile.vue";
import useApplicationInfo from "@/composables/application-info/useApplicationInfo";
import type { EnrollmentTerm } from "@/models/school";


export default /*@__PURE__*/_defineComponent({
  __name: 'PelBorrowAmount',
  props: {
  loading: {
    type: Boolean,
    default: false,
  },
  isCurrent: {
    type: Boolean,
    required: false,
  },
},
  emits: ["complete", "close"],
  setup(__props, { emit: __emit }) {

const store = useStore();
const { formatNumber } = useFormatter();
const { validateBorrowAmount } = useValidation();

const props = __props;

const emit = __emit;

const { editingFromConfirmYourLoanDetails } = useApplicationInfo();
const allowZero = computed<boolean>(() => (productTypeId.value === ProductIds.PEL ? false : true));

const requestedAmount = computed<number | null>(() => {
  if (
    amount.value === undefined ||
    amount.value === null ||
    amount.value === "" ||
    isNaN(Number(amount.value))
  ) {
    return null;
  }
  return Number(amount.value);
});

const requestedAmountChanged = computed<boolean>(() => {
  const original = store.state.loanConfigurationModule?.applicationInfo?.loanAmount;

  if (original === requestedAmount.value) {
    return false;
  }

  return true;
});

const productTypeId = computed<number>(() => store.state.appData?.application?.productTypeId);

const minimumAmount = computed<number>(() =>
  allowZero.value ? 0 : store.getters["PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT"],
);

const programMinimumLoanAmount = computed(() => store.getters["PROGRAM_MINIMUM_LOAN_AMOUNT"]);

const programMinimumLoanAmountDisplay = computed(
  () => store.getters["PROGRAM_MINIMUM_LOAN_AMOUNT_DISPLAY"],
);

const approvedLoanmountDisplay = computed<string>(
  () => `$${formatNumber(store?.state?.appData.application.metadata?.approvedLoanAmount) || ""}`,
);

const approvedLoanAmount = computed(
  () => store.state?.appData?.application?.metadata?.approvedLoanAmount,
);

const selectedEnrollmentTerm = computed(() => {
  return store.state.enrollmentTerms?.find(
    (p: EnrollmentTerm) =>
      p.description === store?.state?.appData?.application?.schoolConfiguration?.academicTerm,
  );
});

const term = computed<string>(
  () => store.state.appData.application.schoolConfiguration.academicTerm,
);

const combinedTerms = computed<boolean>(() =>
  (store.state.appData.application.schoolConfiguration.academicTerm || "")?.includes("-"),
);

const disbursements = computed(() => {
  const terms = term.value?.split("-") || [];
  return terms.map((term: string) => {
    return {
      term: term.trim(),
      amount: (Number(amount.value) || 0) / (terms || []).length,
    };
  });
});

const terms = computed<string>(() => {
  const terms = term.value?.split("-") || [];
  let termString = "";
  for (let i = 0; i < terms.length; i++) {
    if (i === 0) {
      termString += terms[i];
    } else if (i > 0 && i < terms.length - 1) {
      termString += `, ${terms[i]}`;
    } else if (i === terms.length - 1) {
      termString += ` and ${terms[i]}`;
    }
  }
  return termString;
});

const gettingData = ref<boolean>(true);
const updatingApp = ref<boolean>(false);
const error = ref<boolean>(false);
const amount = ref<number | undefined | string>(undefined);
const displayEstimateCalculator = ref<boolean>(false);

let schema = reactive({
  name: "amount",
  type: "money",
  subtype: "initialDrawAmount",
  hideLabel: false,
  required: true,
  allowZero: computed(() => allowZero.value).value,
  errorMessage: computed(() =>
    productTypeId.value === ProductIds.PEL
      ? `Minimum loan amount is ${programMinimumLoanAmountDisplay.value}`
      : "An amount to borrow is required.",
  ).value,
  compareField: "loanAmount",
  minimum: computed(() =>
    productTypeId.value === ProductIds.PEL ? programMinimumLoanAmount.value : minimumAmount.value,
  ).value,
  allowDecimals: false,
  productTypeId: computed(() => productTypeId.value).value,
} as FieldSchema);

function useThisAmount(event: number) {
  amount.value = event;
  validateBorrowAmount(schema, amount.value, approvedLoanAmount.value);
}

async function addInitialDrawRequestAmount() {
  updatingApp.value = true;

  if (requestedAmountChanged.value) {
    const response = await store.dispatch("loanConfigurationModule/addOrUpdateInitialDraw", {
      amount: amount.value,
      editingFromConfirmYourLoanDetails: editingFromConfirmYourLoanDetails.value,
    });

    if (response.applicationId) {
      error.value = false;
    } else {
      updatingApp.value = false;
      error.value = true;
    }
  }
}

async function getInitialDrawRequestAmount() {
  if (
    store.state.loanConfigurationModule.applicationInfo.loanAmount &&
    store.state.loanConfigurationModule.applicationInfo.loanAmount > 0
  ) {
    amount.value = store.state.loanConfigurationModule.applicationInfo.loanAmount;
    schema.valid = true;
    validateBorrowAmount(schema, amount.value, approvedLoanAmount.value);
  } else {
    amount.value = undefined;
  }
}

async function goToRateOptions() {
  updatingApp.value = true;

  if (requestedAmountChanged.value) {
    await addInitialDrawRequestAmount();
  }

  emit("complete", true);
}

function toggleCalculator() {
  displayEstimateCalculator.value = !displayEstimateCalculator.value;
}

function closeModal() {
  emit("close", true);
}

function handleAmountChange(amountStr: string) {
  if (amountStr) {
    amount.value = Number(amountStr);
  }
}

watch(
  () => props.isCurrent,
  async (currentValue: boolean) => {
    await getInitialDrawRequestAmount();
    gettingData.value = false;
    updatingApp.value = false;
  },
);
watch(
  () => amount,
  (currentValue: any) => {
    let value = undefined;
    if (currentValue === "0") {
      value = Number(currentValue);
    } else if (currentValue === "") {
      value = undefined;
    } else {
      value = currentValue;
    }

    validateBorrowAmount(schema, value, approvedLoanAmount.value);
  },
);

onMounted(async () => {
  await getInitialDrawRequestAmount();

  store.dispatch(
    "loanConfigurationModule/setSchoolConfiguration",
    store.state?.appData?.application?.schoolConfiguration,
  );

  gettingData.value = false;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Tile, null, {
    default: _withCtx(() => [
      (gettingData.value || __props.loading)
        ? (_openBlock(), _createBlock(CircleLoader, {
            key: 0,
            inverse: true
          }))
        : (_openBlock(), _createBlock(Step, {
            key: 1,
            class: "stepBtns-flex-end"
          }, {
            header: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createTextVNode(" How Much Do You Need for Your Selected Term? ", -1)
            ]))]),
            actions: _withCtx(() => [
              _createVNode(NextButton, {
                disabled: __props.loading,
                onClick: closeModal,
                text: "Cancel",
                class: "cancel-btn"
              }, null, 8, ["disabled"]),
              _createVNode(NextButton, {
                loading: __props.loading || updatingApp.value,
                disabled: !_unref(schema).valid || __props.loading || updatingApp.value,
                onClick: goToRateOptions,
                text: "Save & Continue"
              }, null, 8, ["loading", "disabled"])
            ]),
            default: _withCtx(() => [
              _cache[7] || (_cache[7] = _createElementVNode("p", { class: "disclaimer" }, " These funds can be used for your school's total cost of attendance which typically includes tuition, fees, books, room & board, food & transportation. ", -1)),
              _createElementVNode("div", _hoisted_1, [
                _withDirectives(_createElementVNode("div", _hoisted_2, [
                  _createElementVNode("p", null, [
                    _cache[3] || (_cache[3] = _createTextVNode(" Amount for ", -1)),
                    _createElementVNode("b", null, _toDisplayString(_unref(store).state.appData?.application?.schoolConfiguration?.academicTerm
                  ? _unref(store).state.appData?.application?.schoolConfiguration?.academicTerm
                  : "Not Yet Selected"), 1),
                    _createTextVNode(" " + _toDisplayString(combinedTerms.value ? "combined" : ""), 1)
                  ]),
                  _createElementVNode("a", { onClick: toggleCalculator }, "How much do I need?")
                ], 512), [
                  [_vShow, !displayEstimateCalculator.value]
                ]),
                _createElementVNode("div", {
                  class: _normalizeClass(["calculator-tooltip", { open: displayEstimateCalculator.value }])
                }, [
                  _createVNode(EstimateCalculator, {
                    term: term.value || '',
                    application: _unref(store).state.appData.application,
                    onUseThisAmount: _cache[0] || (_cache[0] = ($event: any) => (useThisAmount($event))),
                    onClose: toggleCalculator
                  }, null, 8, ["term", "application"])
                ], 2)
              ]),
              _createVNode(BaseFormField, {
                modelValue: amount.value,
                "onUpdate:modelValue": [
                  _cache[1] || (_cache[1] = ($event: any) => ((amount).value = $event)),
                  handleAmountChange
                ],
                schema: _unref(schema),
                compare: approvedLoanAmount.value,
                minimum: minimumAmount.value,
                disabled: updatingApp.value
              }, {
                label: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, [
                    _withDirectives(_createElementVNode("div", _hoisted_4, [
                      _createElementVNode("p", null, [
                        _cache[4] || (_cache[4] = _createTextVNode(" Amount for ", -1)),
                        _createElementVNode("b", null, _toDisplayString(_unref(store).state.appData?.application?.schoolConfiguration?.academicTerm
                      ? _unref(store).state.appData?.application?.schoolConfiguration?.academicTerm
                      : "Not Yet Selected"), 1),
                        _createTextVNode(" " + _toDisplayString(combinedTerms.value ? "combined" : ""), 1)
                      ])
                    ], 512), [
                      [_vShow, displayEstimateCalculator.value]
                    ])
                  ])
                ]),
                _: 1
              }, 8, ["modelValue", "schema", "compare", "minimum", "disabled"]),
              (productTypeId.value === _unref(ProductIds).LOC)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, "(If you are unsure, enter 0.)"))
                : _createCommentVNode("", true),
              (disbursements.value.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _cache[5] || (_cache[5] = _createElementVNode("p", null, "Estimated disbursements to be sent to your school:", -1)),
                    _createElementVNode("div", _hoisted_7, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(disbursements.value, (disbursement, index) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: index,
                          class: "disbursement"
                        }, [
                          _createElementVNode("p", _hoisted_8, "$" + _toDisplayString(_unref(formatNumber)(disbursement.amount)), 1),
                          _createElementVNode("p", _hoisted_9, _toDisplayString(disbursement.term), 1)
                        ]))
                      }), 128))
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_10, [
                _createVNode(Icon, { icon: "info" }),
                (productTypeId.value === _unref(ProductIds).PEL)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_11, [
                      _cache[6] || (_cache[6] = _createTextVNode(" Your maximum loan amount is ", -1)),
                      _createElementVNode("b", null, _toDisplayString(approvedLoanmountDisplay.value), 1)
                    ]))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          }))
    ]),
    _: 1
  }))
}
}

})